// Admin.js
import { useEffect, useState } from "react";
import { auth, logout } from "../firebase";
import { useNavigate, Link } from "react-router-dom";
import { getPageTitle, content } from "../config";
import AdminEventsEditor from "../components/AdminEventsEditor";
import AdminDirectoryEditor from "../components/AdminDirectoryEditor";
import AdminSecurityEditor from "../components/AdminSecurityEditor";
import AdminSecurityMonitor from "../components/AdminSecurityMonitor";
import AdminCommittees from "../components/AdminCommittees";
import AdminExecutiveBoard from "../components/AdminExecutiveBoard";
import AdminPublicSiteEditor from "../components/AdminPublicSiteEditor";
import AdminRentPayments from "../components/AdminRentPayments";
import AdminPaymentRequests from "../components/AdminPaymentRequests";
import AdminFundraisers from "../components/AdminFundraisers";
import AdminPagesMenu from "../components/AdminPagesMenu";
import AdminPageEditor from "../components/AdminPagesEditor";
import AdminNavEditor from "../components/AdminNavEditor";
import AdminNavMenu from "../components/AdminNavMenu";
import AdminAccessSettings from "../components/AdminAccessSettings";
import AdminAnnouncementEditor from "../components/AdminAnnouncementEditor";
import AdminDonations from "../components/AdminDonations";
import AdminEmailMenu from "../components/AdminEmailMenu";
import AdminStaffEditor from "../components/AdminStaffEditor";
import AdminFilesEditor from "../components/AdminFilesEditor";
import AdminResourcesEditor from "../components/AdminResourcesEditor";
import AdminDonationEditor from "../components/AdminDonationEditor";
import AdminLogoEditor from "../components/AdminLogoEditor";
import axios from "axios";
import { toast } from "react-toastify";

export default function Admin() {
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [activeTab, setActiveTab] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    document.title = getPageTitle("Admin Dashboard");
  }, []);

  useEffect(() => {
    const unsub = auth.onAuthStateChanged(async (u) => {
      if (!u) {
        navigate("/admin/login");
        return;
      }

      try {
        const token = await u.getIdToken();

        const res = await axios.get("/api/roles/is-admin", {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (res.data.admin) {
          setUser(u);
        } else {
          navigate("/admin/login");
        }
      } catch (err) {
        toast.error("Error checking admin status");
        navigate("/admin/login");
      } finally {
        setLoading(false);
      }
    });

    return unsub;
  }, [navigate]);

  const handleLogout = async () => {
    await logout();
    navigate("/admin/login");
  };

  if (loading) return null;
  if (!user) return null;

  const menuGroups = [
    {
      title: "Website Editor",
      items: [
        { id: "public-site", label: "Homepage", title: "Homepage Settings", icon: "bi bi-house-door" },
        { id: "logo", label: "Logo", title: "Logo Settings", icon: "bi bi-boxes" },
        { id: "directory", label: "Staff & Board", title: "Staff & Board", icon: "people" },
        { id: "resources", label: "Resources", title: "Resources", icon: "bi bi-newspaper" },
        { id: "donations", label: "Donation Page", title: "Donation Page", icon: "bi bi-piggy-bank" },
        { id: "events", label: "Events", title: "Events Editor", icon: "calendar-event" },
        { id: "pages", label: "Pages", title: "Pages Editor", icon: "bi bi-file-earmark-text" },
        { id: "nav", label: "Navigation", title: "Navbar Editor", icon: "bi bi-compass" },
      ],
    },
    // {
    //   title: "Club Structure",
    //   items: [
    //     { id: "directory", label: "Staff & Board", title: "Staff & Board", icon: "people" },
    //     // { id: "committees", label: "Committees", title: "Committees Editor", icon: "diagram-3" },
    //     // { id: "board", label: "Executive Board", title: "Executive Board Editor", icon: "person-badge" },
    //   ],
    // },
    {
      title: "Communication",
      items: [
        // { id: "announcements", label: "Announcements", title: "Private Member Announcements", icon: "bi bi-megaphone" },
        { id: "email", label: "Email Lists", title: "Email Lists", icon: "bi bi-envelope-at" },
      ],
    },
    // {
    //   title: "Financial",
    //   items: [
    //     { id: "payment-requests", label: "Payment Requests", title: "Payment Requests", icon: "bi bi-wallet" },
    //     { id: "fundraisers", label: "Fundraisers", title: "Fundraisers", icon: "bi bi-cash-stack" },
    //     { id: "donations", label: "Donation Pools", title: "Donation Pools", icon: "bi bi-piggy-bank" }
    //   ]
    // },
    {
      title: "Content Management & Data",
      items: [
        // { id: "announcements", label: "Announcements", title: "Private Member Announcements", icon: "bi bi-megaphone" },
        { id: "files", label: "File Manager", title: "File Manager", icon: "bi bi-archive" },
        { id: "rent", label: "Rent Payments", title: "Rent Payments", icon: "bi bi-house" },
      ],
    },
    {
      title: "Admin Settings",
      items: [
        // { id: "security", label: "Registration Settings", title: "Registration Settings", icon: "shield-lock" },
        { id: "admin-access", label: "Admin Access", title: "Admin Access Settings", icon: "bi bi-key" },
        { id: "security-monitor", label: "Security Monitor", title: "Security Monitor (Advanced)", icon: "bi bi-clipboard-data" },
      ],
    },
  ];

  return (
    <div className="container py-5">
      {/* Header */}
      <div className="d-flex justify-content-between align-items-start mb-4 p-4 primary-border primary-bg">
        <div>
          <h2 className="mb-1 text-white">{content.adminPanelTitle}</h2>
          <a href="/" target="_blank" rel="noopener noreferrer" className="text-decoration-none text-white">Back to site ↗</a>
        </div>
        <button className="btn btn-outline-light" onClick={handleLogout}>
          Log out
        </button>
      </div>

      {/* Welcome Info */}
      <p className="mb-0">Hi, {user.displayName}!</p>
      <p className="mt-0">({user.email})</p>

      {/* Menu or Component */}
      {!activeTab ? (
        <div className="mb-5">
          <div className="row">
            {/* First column - Website Editor */}
            <div className="col-md-6 mb-4">
              {menuGroups[0] && (
                <>
                  <h4 className="mb-3 primary-border pt-3 pb-3 primary-bg text-center" style={{ color: "white" }}>{menuGroups[0].title}</h4>
                  <div className="d-flex flex-wrap gap-3 justify-content-center">
                    {menuGroups[0].items.map((item) => (
                      <div
                        key={item.id}
                        className="card text-center primary-border btn-custom-outline d-flex align-items-center justify-content-center"
                        style={{
                          width: "150px",
                          height: "150px",
                          cursor: "pointer",
                        }}
                        onClick={() => setActiveTab(item)}
                      >
                        <div className="card-body d-flex flex-column align-items-center justify-content-center">
                          <i className={`bi bi-${item.icon} primary-color`} style={{ fontSize: "2rem" }}></i>
                          <h6 className="mt-2 mb-0">{item.label}</h6>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
            
            {/* Second column - All other sections */}
            <div className="col-md-6">
              {menuGroups.slice(1).map((group, i) => (
                <div className="mb-4" key={i}>
                  <h4 className="mb-3 primary-border pt-3 pb-3 primary-bg text-center" style={{ color: "white" }}>{group.title}</h4>
                  <div className="d-flex flex-wrap gap-3 justify-content-center">
                    {group.items.map((item) => (
                  <div
                    key={item.id}
                    className="card text-center primary-border btn-custom-outline d-flex align-items-center justify-content-center"
                    style={{
                      width: "150px",
                      height: "150px",
                      cursor: "pointer",
                    }}
                    onClick={() => setActiveTab(item)}
                  >
                    <div className="card-body d-flex flex-column align-items-center justify-content-center">
                      <i className={`bi bi-${item.icon} primary-color`} style={{ fontSize: "2rem" }}></i>
                      <h6 className="mt-2 mb-0">{item.label}</h6>
                    </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      ) : (
        <>
          <button className="btn btn-link mb-3 small-link" onClick={() => setActiveTab(null)}>
            ← Back to Menu
          </button>

          <div className="container primary-border primary-bg pt-3 pb-2">
            <h3 style={{ color: "white" }}>{activeTab.title}</h3>
          </div>

          <div className="container admin-border pt-3 pb-4">
            {activeTab.id === "events" && <AdminEventsEditor />}
            {activeTab.id === "directory" && <AdminStaffEditor />}
            {activeTab.id === "logo" && <AdminLogoEditor />}
            {activeTab.id === "public-site" && <AdminPublicSiteEditor />}
            {activeTab.id === "announcements" && <AdminAnnouncementEditor />}
            {activeTab.id === "committees" && <AdminCommittees />}
            {activeTab.id === "board" && <AdminExecutiveBoard />}
            {activeTab.id === "payment-requests" && <AdminPaymentRequests />}
            {activeTab.id === "fundraisers" && <AdminFundraisers />}
            {activeTab.id === "pages" && <AdminPageEditor editor={"public"} />}
            {activeTab.id === "nav" && <AdminNavEditor editor={"public"} />}
            {activeTab.id === "email" && <AdminEmailMenu />}
            {activeTab.id === "files" && <AdminFilesEditor />}
            {activeTab.id === "resources" && <AdminResourcesEditor />}
            {activeTab.id === "donations" && <AdminDonationEditor />}
            {activeTab.id === "rent" && <AdminRentPayments />}
            {activeTab.id === "security" && <AdminSecurityEditor />}
            {activeTab.id === "security-monitor" && <AdminSecurityMonitor />}
            {activeTab.id === "admin-access" && <AdminAccessSettings />}
          </div>
        </>
      )}

      <footer className="d-flex mt-3">
        <p className="ms-auto mb-0 me-2 primary-color">Powered by <a className="small-link" href="https://surefirestack.com">Surefire Stack</a></p>
      </footer>

    </div>
  );
}
